import { Card, CardContent } from '@/components/ui/card'
import { itemStatusConfig, type ItemStatus } from '@/constants/item'
import type { DashboardPendingBadges, DashboardSummary } from '@/features/dashboard/types/dashboard'
import { useUI } from '@/hooks/useUI'
import { FileText, Wrench } from 'lucide-react'
import { useRef, type PointerEvent } from 'react'
import { useTranslation } from 'react-i18next'

const summaryKeyByStatus: Record<ItemStatus, keyof DashboardSummary> = {
  Available: 'available',
  InUse: 'inUse',
  Maintenance: 'maintenance',
  Missing: 'missing',
  PendingTransfer: 'pendingTransfer',
  Cleaning: 'cleaning',
  Retired: 'retired',
}

const statsConfig = (
  ['Available', 'InUse', 'Maintenance', 'Missing', 'PendingTransfer', 'Cleaning', 'Retired'] as const
).map(key => {
  const config = itemStatusConfig[key]

  return {
    key,
    colorClass: config.color,
    bgClass: config.bgColor,
    icon: config.icon,
  }
})

const toNumber = (value: number | string | undefined) => Number(value ?? 0)

export function StatsCards({
  summary,
  pendingBadges,
}: {
  summary?: DashboardSummary
  pendingBadges?: DashboardPendingBadges
}) {
  const { t } = useTranslation()
  const { itemStatuses, getEnumLabel } = useUI()
  const scrollRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef({ isDragging: false, startX: 0, scrollLeft: 0 })
  const total = toNumber(summary?.total)
  const pendingTransfers = toNumber(pendingBadges?.pendingTransfers)
  const openRepairs = toNumber(pendingBadges?.openRepairs)

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const scroller = scrollRef.current
    if (!scroller) return

    dragRef.current = {
      isDragging: true,
      startX: event.clientX,
      scrollLeft: scroller.scrollLeft,
    }
    scroller.setPointerCapture(event.pointerId)
  }

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const scroller = scrollRef.current
    if (!scroller || !dragRef.current.isDragging) return

    scroller.scrollLeft = dragRef.current.scrollLeft - (event.clientX - dragRef.current.startX)
  }

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    dragRef.current.isDragging = false
    const scroller = scrollRef.current

    if (scroller?.hasPointerCapture(event.pointerId)) {
      scroller.releasePointerCapture(event.pointerId)
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="dashboard-stats">
      <div
        ref={scrollRef}
        className="overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        onPointerCancel={handlePointerUp}
        onPointerDown={handlePointerDown}
        onPointerLeave={handlePointerUp}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <div className="grid w-full cursor-grab select-none grid-flow-col grid-rows-2 gap-4 active:cursor-grabbing auto-cols-[calc((100%_-_1rem)/2)] lg:grid-rows-1 lg:auto-cols-[calc((100%_-_3rem)/4)]">
          {statsConfig.map(stat => {
            const Icon = stat.icon
            const count = toNumber(summary?.[summaryKeyByStatus[stat.key]])
            const pct = total > 0 ? ((count / total) * 100).toFixed(0) : '0'

            return (
              <Card key={stat.key} className="border-border/50 bg-card">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-muted-foreground">
                        {getEnumLabel(itemStatuses, stat.key, stat.key)}
                      </span>
                      <span className="text-2xl font-bold text-foreground" data-testid={`dashboard-stat-count-${stat.key}`}>{count}</span>
                    </div>
                    <div className={`flex size-10 items-center justify-center rounded-lg ${stat.bgClass}`}>
                      <Icon className={`size-5 ${stat.colorClass}`} />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{t('dashboard.ofTotal', { percent: pct })}</span>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>

      {(pendingTransfers > 0 || openRepairs > 0) && (
        <div className="flex flex-wrap gap-3">
          {pendingTransfers > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2">
              <FileText className="size-4 text-warning" />
              <span className="text-xs text-foreground">
                {t('dashboard.pendingTransferRequests', { count: pendingTransfers })}
              </span>
            </div>
          )}
          {openRepairs > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
              <Wrench className="size-4 text-destructive" />
              <span className="text-xs text-foreground">{t('dashboard.openRepairCases', { count: openRepairs })}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
